import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface StaleSurveyNotification {
  surveyId: string
  reportCount: number
  reportName: string | null
}

export interface ValidSurveyRef {
  id: string
  name: string
}

/**
 * Shared notifications store — acts as the single source of truth for alert state
 * (stale reports today; extensible for other notification types in future).
 *
 * Homepage populates the store after loading site/survey data.
 * NotificationBell reads from it to render the header bell and actions.
 */
export const useNotificationsStore = defineStore('notifications', () => {
  // Stale report data
  const staleCount = ref(0)
  const staleSurveys = ref<StaleSurveyNotification[]>([])

  // Valid surveys (needed for the re-link dropdown)
  const validSurveys = ref<ValidSurveyRef[]>([])

  // Drawer state
  const drawerOpen = ref(false)
  const drawerError = ref<string | null>(null)

  // ── Proposal chat notifications ────────────────────────────────────────────

  /** Number of proposals with unread chat messages */
  const chatMessageCount = ref(0)

  const hasChatAlerts = computed(() => chatMessageCount.value > 0)

  // Computed
  const hasAlerts = computed(() => staleCount.value > 0)
  const totalAlerts = computed(() => staleCount.value + chatMessageCount.value)
  const validSurveyIds = computed(() => validSurveys.value.map(s => s.id))

  function setChatMessageCount(count: number) {
    chatMessageCount.value = count
  }

  // ── Setters (called from HomePage after loading data) ────────────────────

  function setStaleData(count: number, surveys: StaleSurveyNotification[]) {
    staleCount.value = count
    staleSurveys.value = surveys
  }

  function setValidSurveys(surveys: ValidSurveyRef[]) {
    validSurveys.value = surveys
  }

  // ── Drawer actions ────────────────────────────────────────────────────────

  function openStaleReportsDrawer() {
    drawerError.value = null
    drawerOpen.value = true
  }

  function closeDrawer() {
    drawerOpen.value = false
  }

  function setDrawerError(msg: string | null) {
    drawerError.value = msg
  }

  // ── Mutations after API actions ───────────────────────────────────────────

  function removeStale(surveyId: string, countRemoved = 1) {
    staleSurveys.value = staleSurveys.value.filter(s => s.surveyId !== surveyId)
    staleCount.value = Math.max(0, staleCount.value - countRemoved)
    if (staleSurveys.value.length === 0) drawerOpen.value = false
  }

  function clearAllStale() {
    staleSurveys.value = []
    staleCount.value = 0
    drawerOpen.value = false
  }

  return {
    staleCount,
    staleSurveys,
    validSurveys,
    validSurveyIds,
    drawerOpen,
    drawerError,
    hasAlerts,
    totalAlerts,
    setStaleData,
    setValidSurveys,
    openStaleReportsDrawer,
    closeDrawer,
    setDrawerError,
    removeStale,
    clearAllStale,
    // Chat notifications
    chatMessageCount,
    hasChatAlerts,
    setChatMessageCount,
  }
})
